import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsage } from '../context/UsageContext';
import IAPaywall from '../components/IAPaywall';
import { conversationsAPI, chatAPI, rubricsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { cn, formatDate, TAREAS, MODULOS } from '../lib/utils';
import { MODULOS_PARVULARIA, RUBRICAS_PARVULARIA, getIndicadoresParvulariaPorTarea, nivelEsParvularia } from '../lib/parvularia-data';
import { Send, Loader2, Plus, MessageSquare, Trash2, ChevronLeft, Sparkles } from 'lucide-react';
import ContextoPedagogicoBadge from '../components/ContextoPedagogicoBadge';
import IndicadorImpactBanner from '../components/IndicadorImpactBanner';
import { toast } from 'sonner';

export default function Chat() {
    const { conversationId } = useParams();
    const navigate = useNavigate();
    const { user, profile } = useAuth();
    const { puedeUsarIA, refresh: refreshUsage } = useUsage();
    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showNewDialog, setShowNewDialog] = useState(false);
    const [newConvData, setNewConvData] = useState({ titulo: '', tarea: '', indicador: '', indicadorId: '' });
    // Educación Parvularia: se activa por nivel del perfil O por la modalidad elegida (sin exigir el campo Nivel)
    const perfilDocente = profile?.perfil || {};
    const isParvularia = nivelEsParvularia(perfilDocente.nivel) || String(perfilDocente.modalidad_portafolio || '').toLowerCase() === 'parvularia';
    const TAREAS_PV = MODULOS_PARVULARIA.flatMap((m) => m.tareas.map((t) => ({ id: t.id, nombre: t.nombre, titulo: `${m.nombre} · ${t.titulo}` })));
    const tareasVisibles = isParvularia ? TAREAS_PV : TAREAS;
    const [rubrics, setRubrics] = useState([]);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        loadConversations();
        loadRubrics();
    }, []);

    useEffect(() => {
        if (conversationId) {
            loadConversation(conversationId);
        } else {
            setCurrentConversation(null);
        }
    }, [conversationId]);

    useEffect(() => {
        scrollToBottom();
    }, [currentConversation?.mensajes]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadConversations = async () => {
        try {
            const response = await conversationsAPI.getAll();
            setConversations(response.data);
        } catch (error) {
            console.error('Error loading conversations:', error);
        }
    };

    const loadConversation = async (id) => {
        setLoading(true);
        try {
            const response = await conversationsAPI.get(id);
            setCurrentConversation(response.data);
        } catch (error) {
            console.error('Error loading conversation:', error);
            toast.error('Error al cargar la conversación');
        } finally {
            setLoading(false);
        }
    };

    const loadRubrics = async () => {
        try {
            const response = await rubricsAPI.getAll({ activa: true });
            setRubrics(response.data);
        } catch (error) {
            console.error('Error loading rubrics:', error);
        }
    };

    const handleCreateConversation = async () => {
        if (!newConvData.titulo || !newConvData.tarea || !newConvData.indicador) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        try {
            const payload = { titulo: newConvData.titulo, tarea: newConvData.tarea, indicador: newConvData.indicador };
            if (isParvularia) {
                payload.indicador_id = newConvData.indicadorId;
                payload.modalidad = 'parvularia';
            }
            const response = await conversationsAPI.create(payload);
            setConversations([response.data, ...conversations]);
            setShowNewDialog(false);
            setNewConvData({ titulo: '', tarea: '', indicador: '', indicadorId: '' });
            navigate(`/chat/${response.data.id}`);
            toast.success('Conversación creada');
        } catch (error) {
            console.error('Error creating conversation:', error);
            toast.error('Error al crear la conversación');
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!message.trim() || !currentConversation || sending) return;

        const userMessage = message.trim();
        setMessage('');
        setSending(true);

        // Optimistically add user message
        const tempUserMsg = {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString(),
        };

        setCurrentConversation((prev) => ({
            ...prev,
            mensajes: [...(prev.mensajes || []), tempUserMsg],
        }));

        try {
            const response = await chatAPI.send({
                conversation_id: currentConversation.id,
                message: userMessage,
            });

            // Add assistant response
            const assistantMsg = {
                role: 'assistant',
                content: response.data.response,
                timestamp: new Date().toISOString(),
            };

            setCurrentConversation((prev) => ({
                ...prev,
                mensajes: [...(prev.mensajes || []), assistantMsg],
            }));

            loadConversations(); // Refresh sidebar
        } catch (error) {
            console.error('Error sending message:', error);
            if (error?.response?.status === 402) {
                toast.error('El Chat IA requiere un plan activo.');
                if (refreshUsage) refreshUsage();
            } else {
                toast.error('Error al enviar el mensaje');
            }
            // Remove optimistic message on error
            setCurrentConversation((prev) => ({
                ...prev,
                mensajes: prev.mensajes.slice(0, -1),
            }));
        } finally {
            setSending(false);
            textareaRef.current?.focus();
        }
    };

    const handleDeleteConversation = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta conversación?')) return;

        try {
            await conversationsAPI.delete(id);
            setConversations((prev) => prev.filter((c) => c.id !== id));
            if (currentConversation?.id === id) {
                navigate('/chat');
            }
            toast.success('Conversación eliminada');
        } catch (error) {
            console.error('Error deleting conversation:', error);
            toast.error('Error al eliminar la conversación');
        }
    };

    // Opciones de indicador: {value, label, id}. Parvularia usa el dataset pv-* oficial.
    const getIndicadorOptions = (tareaId) => {
        if (isParvularia || String(tareaId).startsWith('pv-')) {
            return getIndicadoresParvulariaPorTarea(tareaId).map((i) => ({ value: i.nombre, label: i.nombre, id: i.id }));
        }
        return getIndicadoresForTarea(tareaId).map((n) => ({ value: n, label: n, id: '' }));
    };
    const onIndicadorChange = (value) => {
        const opt = getIndicadorOptions(newConvData.tarea).find((o) => o.value === value);
        setNewConvData({ ...newConvData, indicador: value, indicadorId: opt?.id || '' });
    };

    const getIndicadoresForTarea = (tareaId) => {
        for (const modulo of MODULOS) {
            const tarea = modulo.tareas.find((t) => t.id === tareaId);
            if (tarea) return tarea.indicadores.map((ind) => ind.nombre);
        }
        return [];
    };

    // Resuelve tareaId/indicadorId desde los nombres almacenados en la conversación
    const resolverContextoConversacion = (conv) => {
        if (!conv) return { tareaId: null, indicadorId: null };
        if (conv.indicador_id && String(conv.indicador_id).startsWith('pv-')) {
            return { tareaId: RUBRICAS_PARVULARIA[conv.indicador_id]?.tarea || null, indicadorId: conv.indicador_id, parvularia: true };
        }
        for (const modulo of MODULOS) {
            const tarea = modulo.tareas.find((t) => t.nombre === conv.tarea);
            if (tarea) {
                const ind = tarea.indicadores.find((i) => i.nombre === conv.indicador);
                return { tareaId: tarea.id, indicadorId: ind?.id || null };
            }
        }
        return { tareaId: null, indicadorId: null };
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    return (
        <Layout>
            {!puedeUsarIA && (
                <div className="mb-4">
                    <IAPaywall feature="Chat IA" />
                </div>
            )}
            <div className="flex h-[calc(100vh-8rem)] gap-4">
                {/* Sidebar - Conversations List */}
                <div className="hidden w-72 flex-shrink-0 flex-col rounded-xl border border-slate-200 bg-white lg:flex">
                    <div className="flex items-center justify-between border-b border-slate-200 p-4">
                        <h2 className="font-heading font-semibold text-slate-900">Conversaciones</h2>
                        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                            <DialogTrigger asChild>
                                <Button size="sm" variant="outline" className="gap-1" data-testid="new-conversation-btn">
                                    <Plus className="h-4 w-4" />
                                    Nueva
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Nueva Conversación</DialogTitle>
                                    <DialogDescription>
                                        Crea una conversación para trabajar un indicador específico
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                    <div className="space-y-2">
                                        <Label>Título</Label>
                                        <Input
                                            placeholder="Ej: Planificación Unidad 1"
                                            value={newConvData.titulo}
                                            onChange={(e) => setNewConvData({ ...newConvData, titulo: e.target.value })}
                                            data-testid="new-conv-title"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tarea</Label>
                                        <Select
                                            value={newConvData.tarea}
                                            onValueChange={(value) => setNewConvData({ ...newConvData, tarea: value, indicador: '' })}
                                        >
                                            <SelectTrigger data-testid="new-conv-tarea">
                                                <SelectValue placeholder="Selecciona una tarea" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {tareasVisibles.map((t) => (
                                                    <SelectItem key={t.id} value={t.id}>
                                                        {t.nombre}: {t.titulo}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Indicador</Label>
                                        <Select
                                            value={newConvData.indicador}
                                            onValueChange={onIndicadorChange}
                                            disabled={!newConvData.tarea}
                                        >
                                            <SelectTrigger data-testid="new-conv-indicador">
                                                <SelectValue placeholder="Selecciona un indicador" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {newConvData.tarea &&
                                                    getIndicadorOptions(newConvData.tarea).map((ind) => (
                                                        <SelectItem key={ind.value} value={ind.value} data-testid={ind.id ? `new-conv-ind-${ind.id}` : undefined}>
                                                            {ind.label}
                                                        </SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        className="w-full"
                                        onClick={handleCreateConversation}
                                        data-testid="create-conv-btn"
                                    >
                                        Crear Conversación
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <ScrollArea className="flex-1">
                        <div className="space-y-1 p-2">
                            {conversations.length > 0 ? (
                                conversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        className={cn(
                                            'group flex items-center gap-2 rounded-lg p-3 cursor-pointer transition-colors',
                                            currentConversation?.id === conv.id
                                                ? 'bg-slate-100'
                                                : 'hover:bg-slate-50'
                                        )}
                                        onClick={() => navigate(`/chat/${conv.id}`)}
                                    >
                                        <MessageSquare className="h-4 w-4 flex-shrink-0 text-slate-400" />
                                        <div className="flex-1 min-w-0">
                                            <p className="truncate text-sm font-medium text-slate-900">
                                                {conv.titulo}
                                            </p>
                                            <p className="truncate text-xs text-slate-500">
                                                {conv.tarea} - {conv.indicador}
                                            </p>
                                        </div>
                                        <button
                                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 transition-opacity"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteConversation(conv.id);
                                            }}
                                        >
                                            <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="p-4 text-center text-sm text-slate-500">
                                    No hay conversaciones
                                </p>
                            )}
                        </div>
                    </ScrollArea>
                </div>

                {/* Main Chat Area */}
                <div className="flex flex-1 flex-col rounded-xl border border-slate-200 bg-white">
                    {currentConversation ? (
                        <>
                            {/* Chat Header */}
                            <div className="flex items-center gap-3 border-b border-slate-200 p-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="lg:hidden"
                                    onClick={() => navigate('/chat')}
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </Button>
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-heading font-semibold text-slate-900 truncate">
                                        {currentConversation.titulo}
                                    </h2>
                                    <p className="text-sm text-slate-500 truncate">
                                        {currentConversation.tarea} - {currentConversation.indicador}
                                    </p>
                                    {(() => {
                                        const ctx = resolverContextoConversacion(currentConversation);
                                        return (
                                            <>
                                                <ContextoPedagogicoBadge
                                                    perfil={profile?.perfil}
                                                    tareaId={ctx.parvularia ? null : ctx.tareaId}
                                                    indicadorId={ctx.parvularia ? null : ctx.indicadorId}
                                                    indicadorNombre={currentConversation.indicador}
                                                    className="mt-2"
                                                />
                                                <IndicadorImpactBanner
                                                    indicadorId={ctx.parvularia ? null : ctx.indicadorId}
                                                    className="mt-3"
                                                />
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Messages */}
                            <ScrollArea className="flex-1 p-4">
                                {loading ? (
                                    <div className="flex h-full items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {currentConversation.mensajes?.length === 0 && (
                                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                                                    <Sparkles className="h-8 w-8 text-slate-400" />
                                                </div>
                                                <h3 className="font-heading font-semibold text-slate-900">
                                                    Comienza la conversación
                                                </h3>
                                                <p className="mt-1 max-w-sm text-sm text-slate-500">
                                                    Escribe tu consulta o pega tu borrador para recibir orientación sobre este indicador.
                                                </p>
                                            </div>
                                        )}
                                        {currentConversation.mensajes?.map((msg, index) => (
                                            <div
                                                key={index}
                                                className={cn(
                                                    'flex chat-message-enter',
                                                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                                                )}
                                            >
                                                <div
                                                    className={cn(
                                                        'max-w-[80%] rounded-2xl px-4 py-3',
                                                        msg.role === 'user'
                                                            ? 'chat-bubble-user'
                                                            : 'chat-bubble-assistant'
                                                    )}
                                                >
                                                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                    <p
                                                        className={cn(
                                                            'mt-1 text-xs',
                                                            msg.role === 'user' ? 'text-slate-300' : 'text-slate-400'
                                                        )}
                                                    >
                                                        {formatDate(msg.timestamp)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                        {sending && (
                                            <div className="flex justify-start">
                                                <div className="chat-bubble-assistant px-4 py-3">
                                                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </ScrollArea>

                            {/* Input */}
                            <form onSubmit={handleSendMessage} className="border-t border-slate-200 p-4">
                                <div className="flex gap-2">
                                    <Textarea
                                        ref={textareaRef}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Escribe tu mensaje o pega tu borrador..."
                                        className="min-h-[44px] max-h-[200px] resize-none"
                                        disabled={sending}
                                        data-testid="chat-input"
                                    />
                                    <Button
                                        type="submit"
                                        size="icon"
                                        disabled={!message.trim() || sending}
                                        data-testid="send-message-btn"
                                    >
                                        {sending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Send className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
                            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
                                <MessageSquare className="h-10 w-10 text-slate-400" />
                            </div>
                            <h2 className="font-heading text-xl font-semibold text-slate-900">
                                Asistente de Evaluación Docente
                            </h2>
                            <p className="mt-2 max-w-md text-slate-500">
                                Selecciona una conversación existente o crea una nueva para comenzar a recibir orientación.
                            </p>
                            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                                <DialogTrigger asChild>
                                    <Button className="mt-6 gap-2" data-testid="start-conversation-btn">
                                        <Plus className="h-4 w-4" />
                                        Nueva Conversación
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Nueva Conversación</DialogTitle>
                                        <DialogDescription>
                                            Crea una conversación para trabajar un indicador específico
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 pt-4">
                                        <div className="space-y-2">
                                            <Label>Título</Label>
                                            <Input
                                                placeholder="Ej: Planificación Unidad 1"
                                                value={newConvData.titulo}
                                                onChange={(e) => setNewConvData({ ...newConvData, titulo: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Tarea</Label>
                                            <Select
                                                value={newConvData.tarea}
                                                onValueChange={(value) => setNewConvData({ ...newConvData, tarea: value, indicador: '' })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Selecciona una tarea" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {tareasVisibles.map((t) => (
                                                        <SelectItem key={t.id} value={t.id}>
                                                            {t.nombre}{isParvularia ? `: ${t.titulo}` : ''}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Indicador</Label>
                                            <Select
                                                value={newConvData.indicador}
                                                onValueChange={onIndicadorChange}
                                                disabled={!newConvData.tarea}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Selecciona un indicador" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {newConvData.tarea &&
                                                        getIndicadorOptions(newConvData.tarea).map((ind) => (
                                                            <SelectItem key={ind.value} value={ind.value}>
                                                                {ind.label}
                                                            </SelectItem>
                                                        ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Button className="w-full" onClick={handleCreateConversation}>
                                            Crear Conversación
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
